import { Injectable } from '@angular/core';
declare let L: any;
import { MapLocation } from '../utils/dashboard-map-types';
import { ParameterService } from '../../../services/parameter/parameter.service';

interface VehicleIconOptions {
  showDetails: boolean;
  licensePlate: string;
  statusColor: string;          // Sol dikdörtgenin rengi
  typeColor: string;            // Orta gövde rengi
  engine: 'I' | 'A';            // Sağdaki yarım daire rengi
  //dotColor: string;           // Ortadaki dairenin iç rengi
  direction?: number;           // Yön (derece cinsinden, 0 = sağa)
  flashLight?: boolean;
}

interface CaseIconOptions {
  typeColor: string;
  statusColor: string;
}

interface GenericIconOptions {
  color: string;
  icon: string;
}

@Injectable({
  providedIn: 'root'
})
export class MapIconServiceService {

  genericColors: {
    appointment: string,
    org: string
  } = {
    appointment: '#fff',
    org: '#fff'
  };

  constructor(private readonly parameterService: ParameterService) {
    this.parameterService.getParameterValues('location-parameters', 'mapPinTypeColor').subscribe(res => {
      const params = res.datas?.values.map((data: any) => {
        return {
          color: data?.valueObject?.color?.value,
          type: data?.valueObject?.type?.value
        }
      });
      this.genericColors = {
        appointment: params.find( p => p.type === 'appointment')?.color,
        org: params.find( p => p.type === 'org')?.color,
      }
    });
  }

  getIcon(location: MapLocation, highlight: boolean = false, showDetails: boolean = false): L.DivIcon {
    switch (location.type) {
      case 'vehicle':
        return this.getVehicleIcon({
          showDetails,
          licensePlate: location.licensePlate ?? '',
          engine: (location.engine as any) ?? 'I',
          statusColor: location.statusColor ?? 'white',
          typeColor: location.typeColor ?? 'white',
          direction: location.direction
        }, highlight);
      case 'case':
        return this.getCaseIcon({
          statusColor: location.statusColor ?? 'white',
          typeColor: location.typeColor ?? 'white',
        }, highlight);
      case 'appointment':
      case 'org':
        return this.getGenericIcon({
          color: this.genericColors[location.type],
          icon: location.type
        }, highlight);
      default:
        return L.divIcon({
          className: '',
          html: '',
          iconSize: [50, 28],
          iconAnchor: [25, 14]
        })
    }
  }

  private getVehicleIcon(options: VehicleIconOptions, highlight: boolean = false): L.DivIcon {
    const {
      statusColor,
      typeColor,
      direction = 0,
      flashLight = false,
      showDetails,
      licensePlate,
    } = options;

    const lightClass = flashLight ? 'emergency-light' : '';

    const detailsHtml = showDetails ? `<div class="${lightClass}" style="
        position: absolute;
        bottom: -12px;
        left: -12px;
        font-size: 11px;
        background-color: ${typeColor};
        color: black;
        padding: 1px 4px;
        border-radius: 4px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.3);
      ">
        ${licensePlate}
      </div>` : '';

    const html = `
    <div style="position: relative; width: 40px; height: 40px;">

      ${detailsHtml}

      <div style="
        position: absolute;
        width: 40px;
        height: 40px;
        background-color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        transform: rotate(-${direction}rad) scale(${highlight ? 1 : 0.7});
      ">
      <div style="
          position: absolute;
          top: 6px;
          left: 34px;
          width: 0;
          height: 0;
          border-top: 14px solid transparent;
          border-bottom: 14px solid transparent;
          border-left: 16px solid ${statusColor};
          clip-path: inset(0 0 0 5.5px);
        "></div>
        <div style="
          width: 32px;
          height: 32px;
          background-color: ${statusColor};
          border-radius: 50%;
        "></div>

      </div>

    </div>
  `;

    return L.divIcon({
      className: '',
      html,
      iconSize: [40, 40],
      iconAnchor: [20,20]
    });
  }

  private getCaseIcon(options: CaseIconOptions, highlight: boolean = false): L.DivIcon {
    const {
      statusColor,
      typeColor
    } = options;

    const html = `
      <div style="
        width: 40px;
        height: 40px;
        background-color: ${statusColor};
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        transform: scale(${highlight ? 1 : 0.8});
      ">
        <div style="
          width: 32px;
          height: 32px;
          background-color: ${typeColor};
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          font-weight: 900;
          color: white;
        ">+</div>
      </div>
    `;

    return L.divIcon({
      className: '',
      html,
      iconSize: [40, 40],
      iconAnchor: [20,20]
    });
  }

  private getGenericIcon(options: GenericIconOptions, highlight: boolean = false) {

    const icon = options.icon === 'org' ?
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="white"><path d="M216 64C229.3 64 240 74.7 240 88L240 128L400 128L400 88C400 74.7 410.7 64 424 64C437.3 64 448 74.7 448 88L448 128L480 128C515.3 128 544 156.7 544 192L544 480C544 515.3 515.3 544 480 544L160 544C124.7 544 96 515.3 96 480L96 192C96 156.7 124.7 128 160 128L192 128L192 88C192 74.7 202.7 64 216 64zM480 496C488.8 496 496 488.8 496 480L496 416L408 416L408 496L480 496zM496 368L496 288L408 288L408 368L496 368zM360 368L360 288L280 288L280 368L360 368zM232 368L232 288L144 288L144 368L232 368zM144 416L144 480C144 488.8 151.2 496 160 496L232 496L232 416L144 416zM280 416L280 496L360 496L360 416L280 416zM216 176L160 176C151.2 176 144 183.2 144 192L144 240L496 240L496 192C496 183.2 488.8 176 480 176L216 176z"/></svg>`
    : `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="white"><path d="M528 320C528 434.9 434.9 528 320 528C205.1 528 112 434.9 112 320C112 205.1 205.1 112 320 112C434.9 112 528 205.1 528 320zM64 320C64 461.4 178.6 576 320 576C461.4 576 576 461.4 576 320C576 178.6 461.4 64 320 64C178.6 64 64 178.6 64 320zM296 184L296 320C296 328 300 335.5 306.7 340L402.7 404C413.7 411.4 428.6 408.4 436 397.3C443.4 386.2 440.4 371.4 429.3 364L344 307.2L344 184C344 170.7 333.3 160 320 160C306.7 160 296 170.7 296 184z"/></svg>`

    const html = `
      <div style="
        width: 40px;
        height: 40px;
        background-color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        transform: scale(${highlight ? 1 : 0.8});
      ">
        <div style="
          width: 32px;
          height: 32px;
          background-color: ${options.color};
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          font-weight: 900;
          color: white;
          padding: 4px;
        ">${icon}</div>
      </div>
    `;

    return L.divIcon({
      className: '',
      html,
      iconSize: [40, 40],
      iconAnchor: [20,20]
    });
  }

}
